import Markdown from "../Markdown"
import Box from "@mui/material/Box"
import MuiCard from "@mui/material/Card"
import CardActions from "@mui/material/CardActions"
import CardContent from "@mui/material/CardContent"
import CardMedia from "@mui/material/CardMedia"
import Typography from "@mui/material/Typography"
import { makeStyles } from "tss-react/mui-compat"
import { MomentFormats } from "app-constants/constants"
import { createPath, ROUTE } from "app-constants/routing"
import AppLink from "components/navigation/AppLink"
import useHeadingStyles from "hooks/useHeadingStyles"
import useLaunchpadContext from "hooks/useLaunchpadContext"
import useRelativeTime from "hooks/useRelativeTime"
import ConventionSession from "models/convention-api/v1/ConventionSession"

const useStyles = makeStyles<{
  size: number
  height: number
  subFontSize: number
  tagColour: string
}>()(
  (theme, p) => ({
    headerRoot: {},
    header: {
      marginBottom: "30px",
    },
    root: {
      display: "flex",
      width: "100%",
      flexDirection: "column",
      flexWrap: "nowrap",
      background:
        "linear-gradient(90.1deg, rgba(4, 112, 200, 0.3) 0.08%, rgba(4, 112, 200, 0.1) 99.91%)",
      margin: "10px 10px 10px 10px",
      "&.MuiPaper-rounded": {
        borderRadius: "10px",
      },
      [theme.breakpoints.up("md")]: {
        minHeight: `${p.height}px`,
        flexDirection: "row",
      },
      [theme.breakpoints.down("xl")]: {
        margin: 0,
      },
    },
    coverContainer: {
      position: "relative",
      [theme.breakpoints.up("md")]: {
        minHeight: `${p.height}px`,
      },
    },
    coverBadge: {
      position: "absolute",
      top: "0px",
      left: "0px",
      padding: "10px",
      height: "39px",
      background: `${p.tagColour}`,
      borderRadius: "10px 0px",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
    },
    media: {
      width: "350px",
      height: `${p.height}px`,
      [theme.breakpoints.down("md")]: {
        width: "100%",
      },
      backgroundSize: "cover",
    },
    cardContentContainer: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      padding: "20px 40px",
      flex: "1 1 auto",
      [theme.breakpoints.down("xl")]: {
        padding: "20px",
      },
    },
    subBox: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
      justifyContent: "space-between",
      [theme.breakpoints.down("xl")]: {
        flexDirection: "column-reverse",
      },
    },
    subtitle: {
      fontFamily: "Lato",
      fontSize: "14px",
      lineHeight: "17px",
      fontWeight: 400,
      color: "#000000",
      letterSpacing: 0,
      marginBottom: "5px",
      [theme.breakpoints.down("xl")]: {
        marginBottom: "5px",
      },
    },
    dateHeader: {
      fontFamily: "Lato",
      fontSize: "14px",
      lineHeight: "17px",
      fontWeight: 400,
      color: "#000000",
      letterSpacing: 0,
      [theme.breakpoints.down("xl")]: {
        marginBottom: "20px",
      },
    },
    title: {
      fontFamily: "Raleway",
      fontSize: "24px",
      lineHeight: "28px",
      fontWeight: 600,
      color: "#000000",
      letterSpacing: 0,
      marginBottom: "20px",
    },
    description: {
      fontFamily: "Lato",
      fontStyle: "normal",
      fontWeight: "normal",
      fontSize: `${p.subFontSize}px`,
      lineHeight: `${p.subFontSize + 3}px`,
    },
    cardActions: {
      justifyContent: "flex-end",
      height: "100%",
      padding: 0,
      margin: "0 -5px -5px 0",
    },
    joinButton: {
      fontFamily: "Lato",
      fontStyle: "normal",
      fontWeight: "bold",
      fontSize: "12px",
      lineHeight: "15px",
      color: theme.palette.primary.main,
      alignSelf: "flex-end",
      padding: "10px",
      [theme.breakpoints.down("xl")]: {
        marginBottom: "-10px",
      },
    },
    textStyle: {
      fontFamily: "Lato",
      fontStyle: "italic",
      fontWeight: "normal",
      fontSize: "16px",
      lineHeight: "19px",
      color: "#FFFFFF",
    },
  }),
)

interface ILaunchpadStageSession {
  session: ConventionSession
  institutionName: string
}

const LaunchpadStageSession = (props: ILaunchpadStageSession) => {
  const { session, institutionName } = props
  const imageUrl = session.promotional_image_url
  const formattedStartTime = session.start_at.format(MomentFormats.startTime)
  const formattedEndTime = session.end_at.format(MomentFormats.endTime)
  const formattedDate = `${formattedStartTime} - ${formattedEndTime}`
  const title = session.name
  const description = session.description

  const { convention } = useLaunchpadContext()

  const { during } = useRelativeTime({
    startAt: session.start_at,
    endAt: session.end_at,
  })

  const sessionStatus = during ? `Live Now` : `Starting Soon`
  const joinButton = during ? `JOIN` : `Go to Stage`

  const size = 300,
    height = 210,
    subFontSize = 16

  const { classes, cx } = useStyles({
    size,
    height,
    subFontSize,
    tagColour: sessionStatus === "Live Now" ? "black" : "#0470C8",
  })

  const { classes: headingClasses } = useHeadingStyles()

  return (
    <div className={classes.headerRoot}>
      <Typography variant="h3" className={cx(headingClasses.microh3, classes.header)}>
        On the Stage
      </Typography>
      <MuiCard className={classes.root} elevation={3}>
        <Box className={classes.coverContainer}>
          <CardMedia image={imageUrl} className={classes.media} />
          {sessionStatus && (
            <Box className={classes.coverBadge}>
              <Typography className={classes.textStyle}>{sessionStatus}</Typography>
            </Box>
          )}
        </Box>
        <CardContent className={classes.cardContentContainer}>
          <Box className={classes.subBox}>
            <Typography className={classes.subtitle}>{institutionName}</Typography>
            <Typography className={classes.dateHeader}>{formattedDate}</Typography>
          </Box>
          <Typography variant="h3" className={classes.title}>
            {title}
          </Typography>
          <Typography className={classes.description}>
            <Markdown allow={["emphasis"]}>{description}</Markdown>
          </Typography>
          <CardActions className={classes.cardActions}>
            <AppLink
              className={classes.joinButton}
              href={createPath({
                path: ROUTE.CONVENTION_SESSION,
                params: {
                  conventionId: convention.id,
                  sessionId: session.id,
                },
                query: {
                  sessionIdToJoin: session.id,
                },
              })}>
              {joinButton}
            </AppLink>
          </CardActions>
        </CardContent>
      </MuiCard>
    </div>
  )
}

export default LaunchpadStageSession
